import { type Plan } from '../types';
import { useUser, useCreateCheckout } from '../hooks/useApi';
import { PlanBadge } from '../components/PlanBadge';
import { useTranslation } from 'react-i18next';

const PLAN_ORDER: Plan[] = ['free', 'mid', 'top'];

function CheckIcon() {
  return (
    <svg className="h-4 w-4 text-primary-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
    </svg>
  );
}

const CARD_STYLES: Record<Plan, {
  border: string;
  bg: string;
  header: string;
  badge: string;
  button: string;
  popular?: boolean;
}> = {
  free: {
    border: 'border-gray-200',
    bg: 'bg-white',
    header: 'bg-gray-50',
    badge: '',
    button: 'bg-gray-900 text-white hover:bg-gray-800'
  },
  mid: {
    border: 'border-primary-300',
    bg: 'bg-white',
    header: 'bg-primary-600',
    badge: 'Most popular',
    button: 'bg-primary-600 text-white hover:bg-primary-700',
    popular: true
  },
  top: {
    border: 'border-gray-200',
    bg: 'bg-white',
    header: 'bg-gray-900',
    badge: '',
    button: 'bg-gray-900 text-white hover:bg-gray-800'
  }
};

export function PricingPage() {
  const { t } = useTranslation('pricing');
  const { data: user } = useUser();
  const { mutate: createCheckout, isPending } = useCreateCheckout();

  const handleUpgrade = (plan: Plan) => {
    if (plan === 'free') return;
    createCheckout(plan);
  };

  return (
    <div className="min-h-full p-4 sm:p-8 animate-fade-in">
      <div className="mx-auto max-w-5xl">
        {/* Header */}
        <div className="mb-10 text-center">
          <h1 className="text-3xl font-bold text-gray-900">{t('title')}</h1>
          <p className="mt-3 text-base text-gray-500 max-w-lg mx-auto">
            {t('subtitle')}
          </p>
        </div>

        {/* Plan cards */}
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('plansHeading')}</h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {PLAN_ORDER.map(plan => {
            const styles = CARD_STYLES[plan];
            const isCurrentPlan = user?.plan === plan;
            const isHigherPlan =
              PLAN_ORDER.indexOf(plan) > PLAN_ORDER.indexOf(user?.plan ?? 'free');
            const limitsRaw = t(`plans.${plan}.limits`, { returnObjects: true });
            const limits = Array.isArray(limitsRaw) ? (limitsRaw as string[]) : [];
            const everyPlanRaw = t('everyPlanFeatures', { returnObjects: true });
            const everyPlanFeatures = Array.isArray(everyPlanRaw) ? (everyPlanRaw as string[]) : [];

            return (
              <div
                key={plan}
                data-testid="pricing-tier"
                data-plan={plan}
                className={`relative flex flex-col rounded-2xl border overflow-hidden transition-all duration-200 shadow-card hover:shadow-card-hover ${styles.border} ${styles.bg} ${
                  styles.popular ? 'ring-2 ring-primary-500 ring-offset-2' : ''
                }`}
              >
                {/* Popular badge */}
                {styles.popular && (
                  <div className="absolute -top-px left-0 right-0 flex justify-center">
                    <span className="rounded-b-lg bg-primary-600 px-4 py-0.5 text-xs font-semibold text-white">
                      {t('mostPopular')}
                    </span>
                  </div>
                )}

                {/* Card header */}
                <div className={`px-6 pt-8 pb-6 ${styles.header}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className={`text-lg font-bold ${plan === 'mid' || plan === 'top' ? 'text-white' : 'text-gray-900'}`}>
                      {t(`plans.${plan}.name`)}
                    </h2>
                    {isCurrentPlan && (
                      <PlanBadge plan={plan} size="sm" />
                    )}
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className={`text-4xl font-bold ${plan === 'mid' || plan === 'top' ? 'text-white' : 'text-gray-900'}`}>
                      {t(`plans.${plan}.price`)}
                    </span>
                    {plan !== 'free' && (
                      <span className={`text-sm ${plan === 'mid' ? 'text-primary-200' : 'text-gray-400'}`}>
                        {t('perMonth')}
                      </span>
                    )}
                  </div>
                  <p className={`mt-1.5 text-sm ${plan === 'mid' || plan === 'top' ? 'text-primary-100/80' : 'text-gray-500'}`}>
                    {t(`plans.${plan}.description`)}
                  </p>
                </div>

                {/* Per-plan volume limits (the only differences between plans) */}
                <div className="flex-1 px-6 py-5">
                  <ul className="space-y-3">
                    {limits.map(limit => (
                      <li key={limit} className="flex items-center gap-2.5">
                        <CheckIcon />
                        <span className="text-sm font-medium text-gray-800">{limit}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Everything else is identical on every plan */}
                  {everyPlanFeatures.length > 0 && (
                    <div className="mt-5 border-t border-gray-100 pt-4">
                      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
                        {t('everyPlanHeading')}
                      </p>
                      <ul className="space-y-2.5">
                        {everyPlanFeatures.map(feature => (
                          <li key={feature} className="flex items-center gap-2.5">
                            <CheckIcon />
                            <span className="text-sm text-gray-600">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* CTA */}
                <div className="px-6 pb-6">
                  {isCurrentPlan ? (
                    <div className="flex w-full items-center justify-center rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm font-semibold text-gray-500">
                      {t('currentPlan')}
                    </div>
                  ) : plan === 'free' ? (
                    <div className="flex w-full items-center justify-center rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm font-medium text-gray-400">
                      {t('freeTier')}
                    </div>
                  ) : (
                    <button
                      onClick={() => handleUpgrade(plan)}
                      disabled={isPending || !isHigherPlan}
                      className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold shadow-sm transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed ${styles.button}`}
                    >
                      {isPending ? (
                        <>
                          <div className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                          {t('redirecting')}
                        </>
                      ) : (
                        t('upgradeTo', { name: t(`plans.${plan}.name`) })
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* FAQ / notes */}
        <div className="mt-12 rounded-2xl border border-gray-200 bg-white p-6 shadow-card">
          <h3 className="text-base font-semibold text-gray-900 mb-4">{t('faq.title')}</h3>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {(t('faq.items', { returnObjects: true }) as { q: string; a: string }[]).map(item => (
              <div key={item.q}>
                <p className="text-sm font-medium text-gray-900">{item.q}</p>
                <p className="mt-1 text-sm text-gray-500">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
